/* eslint-disable no-restricted-imports */
import type { DeviceId, PropertyValues, Transform2D } from '@/core/types';
import type { DeviceType } from '@/devices/types';
import { parseAddress } from '@/plc/address/parser';

import type { ProcessSimulationEngine } from './ProcessSimulationEngine';
import type { RuntimeHost } from '@/plc/runtime/RuntimeHost';
import type { AlarmManager } from '../alarms/AlarmManager';

export interface SimulationContext {
  simulationTimeMs: number;
  deltaTimeMs: number;
  host: RuntimeHost;
  process: ProcessSimulationEngine;
  alarms: AlarmManager;
}

export abstract class VirtualDevice<TState extends Record<string, unknown> = Record<string, unknown>> {
  public id: DeviceId;
  public type: DeviceType;
  public name: string;
  public props: PropertyValues;
  public transform: Transform2D;
  public state: TState;

  constructor(id: DeviceId, type: DeviceType, name: string, props: PropertyValues, transform: Transform2D) {
    this.id = id;
    this.type = type;
    this.name = name;
    this.props = props;
    this.transform = transform;
    this.state = {} as TState;
    this.reset();
  }

  abstract update(context: SimulationContext): void;
  abstract reset(): void;
  
  getState(): TState {
    return this.state;
  }

  // Helper to check if address is analog
  protected isAnalogAddress(propKey: string = 'address'): boolean {
    const addrStr = this.props[propKey];
    if (typeof addrStr === 'string' && addrStr) {
      const addr = parseAddress(addrStr);
      return addr !== null && (addr.size === 'W' || addr.size === 'D');
    }
    return false;
  }

  // Helper to read PLC Output (Q)
  protected readPlcOutput(host: RuntimeHost, propKey: string = 'address'): boolean {
    const addrStr = this.props[propKey];
    if (typeof addrStr === 'string' && addrStr) {
      const addr = parseAddress(addrStr);
      if (addr && addr.size === 'X') {
        if (addr.area === 'Q') {
          return host.pi.outputs.getBit(addr.byteOffset, addr.bitOffset);
        }
        if (addr.area === 'M') {
          return host.pi.memory.getBit(addr.byteOffset, addr.bitOffset);
        }
      }
    }
    return false;
  }

  // Helper to write to PLC Input (I)
  protected writePlcInput(host: RuntimeHost, value: boolean, propKey: string = 'address'): void {
    const addrStr = this.props[propKey];
    if (typeof addrStr === 'string' && addrStr) {
      const addr = parseAddress(addrStr);
      if (addr && addr.area === 'I' && addr.size === 'X') {
        const mask = 1 << addr.bitOffset;
        const current = host.physicalInputs[addr.byteOffset];
        if (current !== undefined) {
          if (value) {
            host.physicalInputs[addr.byteOffset] = current | mask;
          } else {
            host.physicalInputs[addr.byteOffset] = current & ~mask;
          }
        }
      }
    }
  }

  // Helper to write to PLC Analog Input (IW or MW)
  protected writePlcAnalogInput(host: RuntimeHost, value: number, propKey: string = 'address'): void {
    const addrStr = this.props[propKey];
    if (typeof addrStr === 'string' && addrStr) {
      const addr = parseAddress(addrStr);
      if (addr && (addr.size === 'W' || addr.size === 'D')) {
        if (addr.area === 'I') {
          const view = new DataView(host.physicalInputs.buffer, host.physicalInputs.byteOffset, host.physicalInputs.byteLength);
          if (addr.size === 'W') {
            view.setInt16(addr.byteOffset, value, false); // Big-endian signed
          } else {
            view.setInt32(addr.byteOffset, value, false); // Big-endian signed
          }
        } else if (addr.area === 'M') {
          if (addr.size === 'W') {
            host.pi.memory.setInt(addr.byteOffset, value);
          } else {
            host.pi.memory.setDWord(addr.byteOffset, value);
          }
        }
      }
    }
  }

  // Helper to read from PLC Analog Output (QW or MW)
  protected readPlcAnalogOutput(host: RuntimeHost, propKey: string = 'address'): number {
    const addrStr = this.props[propKey];
    if (typeof addrStr === 'string' && addrStr) {
      const addr = parseAddress(addrStr);
      if (addr && (addr.size === 'W' || addr.size === 'D')) {
        if (addr.area === 'Q') {
          if (addr.size === 'W') {
            return host.pi.outputs.getInt(addr.byteOffset);
          } else {
            return host.pi.outputs.getDWord(addr.byteOffset);
          }
        } else if (addr.area === 'M') {
          if (addr.size === 'W') {
            return host.pi.memory.getInt(addr.byteOffset);
          } else {
            return host.pi.memory.getDWord(addr.byteOffset);
          }
        }
      }
    }
    return 0;
  }
}
